import { Fragment, useContext, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Trans } from '@lingui/macro'
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import OrganizationIcon from '../OrganizationIcon'
import { useRoutes } from '../../store/routesContext'
import { useActionLogs } from '../../store/actionLogsContext'
import SvgCustomIcon from '../icons/SvgCustomIcon'
import { ThemeContext } from '../../App'
import { useDesktopSidebar } from '../../contexts/desktop-sidebar-context'
import { useOfflineNavActions } from '../../contexts/offline-nav-actions-context'
import {
  getOfflineDataNavItem,
  getOfflineDataSubItems,
  OFFLINE_DATA_NAV_ID,
} from '../offline/offline-data-nav-items'
import useOnlineStatus from '../../hooks/useOnlineStatus'
import { canUserAccessActionHistory } from '../../pages/Statistics/statistics-report-type'
import { formatHistoryCountDisplay } from '../../utils/format-history-count'

/**
 * Voce menu principale attiva (stesso criterio per evidenziazione e sottomenu).
 */
function isSectionActive(elem, location) {
  const { pathname, state } = location
  const historyOpen = state?.isHistoryOpen === true
  if (elem.route === '/history') {
    return pathname === '/history' || (pathname === '/home' && historyOpen)
  }
  if (elem.route === '/home') {
    return pathname === '/home' && !historyOpen
  }
  return pathname === elem.route
}

export default function DesktopNav({
  position: _routePositionIndex,
  subPanelElements,
  isStatsPanelOpen = false,
  isDrawingPolygon = false,
}) {
  const routes = useRoutes()
  const theme = useTheme()
  const location = useLocation()
  const { state = {} } = location
  const persistedInit = useMemo(() => {
    try {
      const raw = localStorage.getItem('adams_user')
      const user = raw ? JSON.parse(raw) : null
      return user?.init ?? {}
    } catch {
      return {}
    }
  }, [])
  const init = state?.init ?? persistedInit
  const navUser = init?.user ?? {}
  const navigate = useNavigate()
  const [switches, setSwitches] = useState(() => subPanelElements?.map(() => false))
  const { count: newLogsCount } = useActionLogs()
  const { mode } = useContext(ThemeContext)
  const {
    collapsed,
    toggleCollapsed,
    effectiveWidthPx,
    beginResize,
  } = useDesktopSidebar()
  const isOnline = useOnlineStatus()
  const navLockedOffline = !isOnline
  const offlineNavActions = useOfflineNavActions()
  const [offlineDataExpanded, setOfflineDataExpanded] = useState(false)

  const handleListItemClick = (_, route) => {
    if (navLockedOffline && route !== '/home') {
      return
    }
    if (route === '/history') {
      navigate('/home', { state: { ...state, isHistoryOpen: true } })
      return
    }
    if (route === location.pathname) {
      return
    }
    navigate(route, { state: { ...state, isHistoryOpen: false } })
  }

  const mainPanelElementsProps = [
    {
      label: <Trans>Data visualization</Trans>,
      icon: <SvgCustomIcon name={'home'} size={20} />,
      route: '/home',
      subPanelsNumber: 6,
      access: [0, 1, 2, 3],
    },
    {
      label: <Trans>Admin</Trans>,
      icon: <SvgCustomIcon name={'shieldPerson'} size={20} />,
      route: '/admin',
      subPanelsNumber: 3,
      access: [1, 2, 3],
    },
    {
      label: <Trans>Data analysis</Trans>,
      icon: <SvgCustomIcon name={'barChart3'} size={20} />,
      route: '/statistics',
      subPanelsNumber: 2,
      access: [0, 1, 2, 3],
    },
    {
      label: <Trans>Notification</Trans>,
      icon: <NotificationsNoneOutlinedIcon sx={{ fontSize: 20 }} />,
      route: '/history',
      subPanelsNumber: 0,
      access: [0,1, 2, 3],
      badge: newLogsCount,
    },
    {
      label: <Trans>Help</Trans>,
      icon: <SvgCustomIcon name={'help'} size={20} />,
      route: '/help',
      subPanelsNumber: 0,
      access: [0, 1, 2, 3],
    },
    {
      label: <Trans>Data merging and validation</Trans>,
      icon: <SvgCustomIcon name={'merge'} size={20} />,
      subPanelsNumber: 0,
      route: '/tools',
      access: [1],
    },
  ]

  const mainPanelElements = routes.map((route) =>
    mainPanelElementsProps.find((element) => element.route === route) || { route },
  )

  const offlineSubItems = useMemo(
    () =>
      getOfflineDataSubItems({
        userType: navUser.type,
        isOnline,
        actions: offlineNavActions,
        navigate,
        locationState: state,
        pathname: location.pathname,
      }),
    [
      navUser.type,
      isOnline,
      offlineNavActions,
      navigate,
      state,
      location.pathname,
    ],
  )

  const navItems = useMemo(() => {
    let filtered = mainPanelElements.filter((elem) =>
      elem.access?.includes(navUser.type),
    )
    if (!canUserAccessActionHistory(navUser)) {
      filtered = filtered.filter((elem) => elem.route !== '/history')
    }
    if (offlineSubItems.length === 0) {
      return filtered
    }
    const helpIndex = filtered.findIndex((elem) => elem.route === '/help')
    const offlineItem = getOfflineDataNavItem()
    if (helpIndex === -1) {
      return [...filtered, offlineItem]
    }
    return [
      ...filtered.slice(0, helpIndex),
      offlineItem,
      ...filtered.slice(helpIndex),
    ]
  }, [mainPanelElements, navUser, offlineSubItems.length])

  const getUsername = () => {
    const fullName = navUser?.name + ' ' + navUser?.surname
    if (fullName) {
      if (fullName.length > 25) {
        return fullName.substring(0, 20) + '...'
      }
      return fullName
    }
    return navUser?.surname
  }

  const isRtl = theme.direction === 'rtl'
  const toolbarH = theme.mixins.toolbar.minHeight

  const selectedRowSx = {
    borderInlineStart: '3px solid transparent',
    '&.Mui-selected': {
      backgroundColor:
        mode === 'dark'
          ? 'rgba(246, 213, 82, 0.18)'
          : 'rgba(25, 118, 210, 0.14)',
      borderInlineStart: `3px solid ${theme.palette.primary.main}`,
      '&:hover': {
        backgroundColor:
          mode === 'dark'
            ? 'rgba(246, 213, 82, 0.26)'
            : 'rgba(25, 118, 210, 0.2)',
      },
    },
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        anchor={isRtl ? 'right' : 'left'}
        sx={{
          width: effectiveWidthPx,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: effectiveWidthPx,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Toolbar
            sx={{
              height: toolbarH,
              minHeight: toolbarH,
              justifyContent: collapsed ? 'center' : 'space-between',
              alignItems: 'center',
              display: 'flex',
              px: collapsed ? 0 : '14px',
            }}
          >
            {collapsed ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <OrganizationIcon
                  organization={navUser.organization}
                  userType={navUser.type}
                  style={{ color: '#4a4a4a', fontSize: 28 }}
                />
                <Tooltip title={<Trans>Expand menu</Trans>} placement={isRtl ? 'left' : 'right'}>
                  <IconButton
                    size="small"
                    onClick={toggleCollapsed}
                    aria-label="expand navigation"
                    sx={{ p: 0.25 }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ flex: 0.4, display: 'flex' }}>
                    <OrganizationIcon
                      organization={navUser.organization}
                      userType={navUser.type}
                      style={{ color: '#4a4a4a', fontSize: 24 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', marginInlineStart: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '10px', sm: '11px', md: '12px', lg: '13px', xl: '14px' },
                        fontWeight: 700,
                        marginInlineStart: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getUsername()}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '8px', sm: '9px', md: '10px', lg: '11px', xl: '12px' },
                        marginInlineStart: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {navUser.department}
                      {navUser.type === 3 && navUser.user_organizations_string && (
                        <> · {navUser.user_organizations_string}</>
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Tooltip title={<Trans>Collapse menu</Trans>}>
                  <IconButton
                    size="small"
                    onClick={toggleCollapsed}
                    aria-label="collapse navigation"
                    sx={{ flexShrink: 0 }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Toolbar>

          <Box sx={{ overflow: 'auto', flex: 1 }}>
            {Object.keys(init || {}).length > 0 && (
              <>
                <Divider />
                <List sx={{ py: 0.5 }}>
                  {navItems.map((elem) => {
                      const isOfflineSection = elem.route === OFFLINE_DATA_NAV_ID
                      const isHistoryOpen = location.state?.isHistoryOpen ?? false
                      const isDisabled = isOfflineSection
                        ? isHistoryOpen || isStatsPanelOpen || isDrawingPolygon
                        : isHistoryOpen ||
                          isStatsPanelOpen ||
                          isDrawingPolygon ||
                          (navLockedOffline && elem.route !== '/home')
                      const isSubMenuDisabled = isStatsPanelOpen || isDrawingPolygon
                      const subPanelLockedOffline = navLockedOffline
                      const active = isOfflineSection
                        ? location.pathname === '/device-provision' ||
                          location.pathname === '/package-imports' ||
                          offlineDataExpanded
                        : isSectionActive(elem, location)
                      const submenuOpen = isOfflineSection
                        ? offlineDataExpanded ||
                          location.pathname === '/device-provision' ||
                          location.pathname === '/package-imports'
                        : active &&
                          elem.subPanelsNumber > 0 &&
                          !(elem.route === '/home' && isHistoryOpen)
                      const hasActiveSubItem = isOfflineSection
                        ? location.pathname === '/device-provision' ||
                          location.pathname === '/package-imports'
                        : submenuOpen &&
                          subPanelElements.some(
                            (el) => el.type === 'button' && el.active === true,
                          )

                      const listButton = (
                        <ListItemButton
                          selected={active && !hasActiveSubItem}
                          onClick={() => {
                            if (isOfflineSection) {
                              setOfflineDataExpanded((expanded) => !expanded)
                              return
                            }
                            handleListItemClick(null, elem.route)
                          }}
                          disabled={isDisabled}
                          sx={{
                            width: '100%',
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            px: collapsed ? 1 : 2,
                            ...selectedRowSx,
                          }}
                        >
                          {elem.route === '/history' ? (
                            <Box
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              width="100%"
                              justifyContent={collapsed ? 'center' : 'flex-start'}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                {elem.icon}
                                {!collapsed && (
                                  <Typography fontSize={15} sx={{ marginInlineStart: 2 }}>
                                    {elem.label}
                                  </Typography>
                                )}
                              </Box>
                              {!collapsed && elem.badge > 0 && (
                                <Box
                                  sx={{
                                    minWidth: 22,
                                    height: 22,
                                    borderRadius: '999px',
                                    background: theme.palette.newColors.pinkGradient,
                                    color: 'white',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginInlineStart: 'auto',
                                    px: 1,
                                  }}
                                >
                                  {formatHistoryCountDisplay(elem.badge)}
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Box
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              width="100%"
                              position="relative"
                              justifyContent={collapsed ? 'center' : 'flex-start'}
                            >
                              {elem.icon}
                              {!collapsed && (
                                <>
                                  <Typography fontSize={15} flex={2} sx={{ marginInlineStart: 2 }}>
                                    {elem.label}
                                  </Typography>
                                  {elem.subPanelsNumber > 0 ? (
                                    submenuOpen ? (
                                      <KeyboardArrowUpIcon sx={{ position: 'absolute', insetInlineEnd: 8 }} />
                                    ) : (
                                      <KeyboardArrowDownIcon sx={{ position: 'absolute', insetInlineEnd: 8 }} />
                                    )
                                  ) : null}
                                </>
                              )}
                            </Box>
                          )}
                        </ListItemButton>
                      )

                      return (
                        <Fragment key={elem.route}>
                          <ListItem disablePadding sx={{ display: 'block' }}>
                            <Tooltip
                              title={collapsed ? elem.label : ''}
                              placement={isRtl ? 'left' : 'right'}
                              disableHoverListener={!collapsed}
                            >
                              <Box component="span" sx={{ display: 'block' }}>
                                {listButton}
                              </Box>
                            </Tooltip>
                          </ListItem>
                          <Collapse
                            sx={{
                              boxShadow: 'none',
                              borderInlineStart: collapsed ? 'none' : '0.5px solid lightgrey',
                              ...(collapsed && {
                                marginInlineStart: '0 !important',
                                marginInlineEnd: '0 !important',
                                boxShadow: 'none !important',
                              }),
                            }}
                            in={submenuOpen}
                            timeout="auto"
                            unmountOnExit
                          >
                            {isOfflineSection ? (
                              <>
                                <List sx={{ py: collapsed ? 0 : undefined }}>
                                  {offlineSubItems.map((subEl) => {
                                    const subDisabled =
                                      isSubMenuDisabled ||
                                      subEl.disabled ||
                                      (subEl.key === 'prepare' && subPanelLockedOffline) ||
                                      (subEl.key === 'import' && subPanelLockedOffline)
                                    const subButton = (
                                      <ListItemButton
                                        selected={subEl.active === true}
                                        disabled={subDisabled}
                                        onClick={subEl.action}
                                        sx={{
                                          justifyContent: collapsed ? 'center' : 'flex-start',
                                          px: collapsed ? 1 : 4,
                                          py: 1,
                                          opacity: subDisabled ? 0.5 : 1,
                                          cursor: subDisabled ? 'not-allowed' : 'pointer',
                                          ...selectedRowSx,
                                        }}
                                      >
                                        <ListItemIcon
                                          sx={{
                                            minWidth: 'auto',
                                            marginInlineEnd: collapsed ? 0 : 1,
                                            justifyContent: 'center',
                                          }}
                                        >
                                          {subEl.icon}
                                        </ListItemIcon>
                                        {!collapsed && (
                                          <Typography sx={{ fontSize: 14, marginInlineStart: 2 }}>
                                            {subEl.label}
                                          </Typography>
                                        )}
                                      </ListItemButton>
                                    )
                                    return (
                                      <ListItem disablePadding key={subEl.key}>
                                        <Tooltip
                                          title={collapsed ? subEl.label : ''}
                                          placement={isRtl ? 'left' : 'right'}
                                          disableHoverListener={!collapsed}
                                        >
                                          <Box
                                            component="span"
                                            sx={{ display: 'block', width: '100%' }}
                                          >
                                            {subButton}
                                          </Box>
                                        </Tooltip>
                                      </ListItem>
                                    )
                                  })}
                                </List>
                                {!collapsed && <Divider />}
                              </>
                            ) : (
                              !!subPanelElements.length && (
                                <>
                                  <List sx={{ py: collapsed ? 0 : undefined }}>
                                    {subPanelElements
                                      .filter((el) => el.access?.includes(navUser.type))
                                      .filter(
                                        (el) =>
                                          !el.baseOrgAccess ||
                                          navUser.type === 1 ||
                                          el.baseOrgAccess.includes(navUser.basic_organization) ||
                                          (navUser.type === 3 &&
                                            (navUser.organization_ids || []).some((orgId) =>
                                              el.baseOrgAccess.includes(orgId),
                                            )),
                                      )
                                      .map((subEl, subIndex) => {
                                        if (subEl.type === 'switch') {
                                          if (collapsed) return null
                                          return (
                                            <ListItem key={`sw-${subIndex}`} sx={{ paddingInlineStart: 4 }}>
                                              <FormControlLabel
                                                sx={{
                                                  '& .MuiFormControlLabel-label': {
                                                    fontSize: '14px',
                                                  },
                                                  opacity: isSubMenuDisabled || subPanelLockedOffline ? 0.5 : 1,
                                                }}
                                                disabled={isSubMenuDisabled || subPanelLockedOffline}
                                                // Se l'elemento porta il proprio stato "checked" (fonte di verità
                                                // esterna, es. isMerged/isUnmerged in Home.js), usarlo sempre al
                                                // posto dello stato locale: evita che lo switch visuale resti
                                                // desincronizzato dal filtro realmente applicato ai pin.
                                                checked={subEl.checked ?? switches[subIndex]}
                                                control={<Switch />}
                                                label={subEl.label}
                                                onChange={(e) => {
                                                  setSwitches((old) =>
                                                    old.map((o, i) => (i === subIndex ? e.target.checked : o)),
                                                  )
                                                  subEl.action(e.target.checked)
                                                }}
                                              />
                                            </ListItem>
                                          )
                                        }
                                        if (subEl.type === 'button') {
                                          const subButton = (
                                            <ListItemButton
                                              selected={subEl.active === true}
                                              disabled={isSubMenuDisabled}
                                              onClick={subEl.action}
                                              sx={{
                                                justifyContent: collapsed ? 'center' : 'flex-start',
                                                px: collapsed ? 1 : 4,
                                                py: 1,
                                                opacity: isSubMenuDisabled ? 0.5 : 1,
                                                cursor: isSubMenuDisabled ? 'not-allowed' : 'pointer',
                                                ...selectedRowSx,
                                              }}
                                            >
                                              <ListItemIcon sx={{ minWidth: 'auto', marginInlineEnd: collapsed ? 0 : 1, justifyContent: 'center' }}>
                                                {subEl.icon}
                                              </ListItemIcon>
                                              {!collapsed && (
                                                <Typography sx={{ fontSize: 14, marginInlineStart: 2 }}>
                                                  {subEl.label}
                                                </Typography>
                                              )}
                                            </ListItemButton>
                                          )
                                          return (
                                            <ListItem disablePadding key={`btn-${subIndex}`}>
                                              <Tooltip
                                                title={collapsed ? subEl.label : ''}
                                                placement={isRtl ? 'left' : 'right'}
                                                disableHoverListener={!collapsed}
                                              >
                                                <Box component="span" sx={{ display: 'block', width: '100%' }}>
                                                  {subButton}
                                                </Box>
                                              </Tooltip>
                                            </ListItem>
                                          )
                                        }
                                        return null
                                      })}
                                  </List>
                                  {!collapsed && <Divider />}
                                </>
                              )
                            )}
                          </Collapse>
                        </Fragment>
                      )
                    })}
                </List>
                <Divider />
              </>
            )}
          </Box>

          {!collapsed && (
            <Box
              onMouseDown={beginResize}
              role="separator"
              aria-orientation="vertical"
              aria-label="resize navigation"
              sx={{
                position: 'absolute',
                top: toolbarH,
                bottom: 0,
                width: 6,
                insetInlineEnd: 0,
                cursor: 'col-resize',
                zIndex: 2,
                touchAction: 'none',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  )
}
